import { ReactNode } from "react";
import withLayout from "../utils/withLayout";
import NotFound from "../pages/ErrorPages/NotFound";
import Forbidden from "../pages/ErrorPages/Forbidden";
import Home from "../pages";
import ParticipantProfilePage from "../pages/participants/profile";
import rewardsConfigPage from "../pages/rewardsConfiguration";
import DashboardPage from "@/pages/dashboard";
import HelpAndSupport from "@/pages/helpAndSupport";

// Management pages
import OrganizationsPage from "@/pages/management/organizations";
import ProgramsPage from "@/pages/management/programs";
import CreateProgramPage from "@/pages/management/programs/create";
import RolesPage from "@/pages/management/roles";
import EditProgramPage from "@/pages/management/programs/edit";
import UsersPage from "../pages/management/users";
import ActionsPage from "@/pages/management/actions";
import EducationalResourcesPage from "@/pages/management/educationalResources";
import ParticipantPage from "@/pages/participants";
import ReportsPage from "@/pages/reports";
import GroupLabelPage from "@/pages/management/groupLabel";
import SettingsPage from "@/pages/settings";
import AuditLogsPage from "@/pages/auditlogs";
import AppVersionsPage from "@/pages/management/app-versions";
import GeofencesPage from "@/pages/geofences";
import GeofenceDetailPage from "@/pages/geofences/detail";
import ExportDataPage from "@/pages/export-data";

export interface RouteItem {
  path: string; // Path of the route
  element?: ReactNode; // Element to render for this route (optional for parent routes with children)
  requiresAuth?: boolean; // Whether authentication is required
  requiredActions?: string[]; // Required actions to access the route
  children?: RouteItem[]; // Nested routes (children)
}

//TODO: we should extract the known routes from the db so they are dynamic
export const routesConfig: RouteItem[] = [
  {
    path: "/",
    element: withLayout(Home)({}) as ReactNode as ReactNode,
    requiresAuth: false,
    requiredActions: ["DASHBOARD_VIEW"],
  },
  {
    path: "/organizations",
    element: withLayout(OrganizationsPage)({}) as ReactNode,
    requiredActions: ["OU_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/grouplabels",
    element: withLayout(GroupLabelPage)({}) as ReactNode,
    requiredActions: ["GROUPLABEL_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/programs",
    element: withLayout(ProgramsPage)({}) as ReactNode,
    requiredActions: ["PROG_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/roles",
    element: withLayout(RolesPage)({}) as ReactNode,
    requiredActions: ["ROLES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/programs/create",
    element: withLayout(CreateProgramPage)({}) as ReactNode,
    requiredActions: ["PROG_ADD"],
    requiresAuth: true,
  },
  {
    path: "/programs/:programId",
    element: withLayout(EditProgramPage)({}) as ReactNode,
    requiredActions: ["PROG_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants",
    element: withLayout(ParticipantPage)({}) as ReactNode,
    requiredActions: ["PARTICIPANTS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/participants/:participantId",
    element: withLayout(ParticipantProfilePage)({}) as ReactNode,
    requiredActions: ["PARTICIPANT_PROFILE_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/users",
    element: withLayout(UsersPage)({}) as ReactNode,
    requiredActions: ["USERS_LIST_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/actions",
    element: withLayout(ActionsPage)({}) as ReactNode,
    requiredActions: ["ACTIONS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/educational-resources",
    element: withLayout(EducationalResourcesPage)({}) as ReactNode,
    requiredActions: ["EDUCATIONAL_RESOURCES_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/settings",
    element: withLayout(SettingsPage)({}) as ReactNode,
    requiredActions: ["SETTINGS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/app-versions",
    element: withLayout(AppVersionsPage)({}) as ReactNode,
    requiredActions: ["APP_VERSIONS_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/rewardsConfiguration",
    element: withLayout(rewardsConfigPage)({}) as ReactNode,
    requiredActions: ["REWARDS_CONFIGURATION_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/dashboard",
    element: withLayout(DashboardPage)({}) as ReactNode,
    requiredActions: ["DASHBOARD_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/reports",
    element: withLayout(ReportsPage)({}) as ReactNode,
    requiredActions: ["REPORT_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/help-and-support",
    element: withLayout(HelpAndSupport)({}) as ReactNode,
    requiredActions: ["HELP_SUPPORT_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/export-data",
    element: withLayout(ExportDataPage)({}) as ReactNode,
    requiredActions: ["EXPORT_DATA_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/geofences",
    element: withLayout(GeofencesPage)({}) as ReactNode,
    requiredActions: ["GEO_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/geofences/:geofenceId",
    element: withLayout(GeofenceDetailPage)({}) as ReactNode,
    requiredActions: ["GEO_VIEW"],
    requiresAuth: true,
  },
  {
    path: "/auditlogs",
    element: withLayout(AuditLogsPage)({}) as ReactNode,
    requiredActions: ["DASHBOARD_VIEW"], // Los QAs acceden directamente a la ruta
    requiresAuth: true,
  },
  {
    path: "/403",
    element: withLayout(Forbidden, true)({}) as ReactNode,
    requiresAuth: false,
  },
  {
    path: "/404",
    element: withLayout(NotFound, true)({}) as ReactNode,
    requiresAuth: false,
  },
];

export const isKnownRoute = (path: string): boolean => {
  const routePatterns = routesConfig.flatMap((route: RouteItem) => {
    const childPatterns =
      route.children
        ?.filter((child) => child.element)
        .map((child) => `${route.path.replace(/\/$/, "")}/${child.path}`) || [];

    return route.element ? [route.path, ...childPatterns] : childPatterns;
  });

  const regexPatterns = routePatterns.map(
    (routePattern) => new RegExp(`^${routePattern.replace(/:\w+/g, "\\w+")}$`),
  );

  return regexPatterns.some((regex) => regex.test(path));
};
